import type { StateClassKey } from "@tdelivr/design-tokens";

import type { DriverProfile } from "@tdelivr/api-client/generated/driver/driver-profile.js";

import type { BoardRow } from "./board.ts";

/**
 * Driver onboarding: the tenant's couriers and whether their documents let them work.
 *
 * ## Why this board exists, and why it had to be invented
 *
 * Document 01 §265 says a tenant must be able to "manage drivers". Nothing on this platform let
 * anyone do the part that matters. DRV-03 publishes
 * `POST /v1/drivers/{driverId}/documents/{documentId}/verification-decisions` under
 * `driver-document:verify`, and **no screen in any application spent that scope**, so under ADR
 * 0032 no client held it — correctly, because a grant is derived from what screens spend.
 *
 * The consequence was total and narrow. EXP-003's readiness gate blocks a courier on
 * `no_documents`, so no signed-in courier could go online, accept an offer or run a trip, on a
 * deployment where every other surface worked. The design specifies the courier's entire side of
 * onboarding — the onboarding board, the vehicle screen, the document upload, all three built — and
 * its copy says "We're checking your documents. Most reviews finish within 1 business day" without
 * ever saying which screen does the checking. Recorded as **Q-236**.
 *
 * ## This screen has no design, and says so
 *
 * `GUI_Design/SCREEN_SPEC.md` does not list it, and `GUI_Design/design/` draws no page for it. So
 * nothing here is copied from a design file and nothing here may claim to be: CLAUDE.md §1 governs
 * screens that *have* designs, and this one applies the console's own established conventions
 * instead — `RecordsBoard`, the header kit, the grid toolbar, the state badge vocabulary — so that
 * it is recognisably this console rather than a second visual language.
 *
 * It therefore sits **outside** the 110-of-114 screen ratio, exactly as the four analytics boards
 * do under Q-186: adding to a numerator what the denominator cannot hold is how a meter lies. Every
 * string below that a design would normally supply is marked in the board's own footnote.
 *
 * ## What a row is, and what it deliberately is not
 *
 * One row per driver profile, not per document. An administrator's question is "can this courier
 * work", and that is a property of the whole set — one expired licence blocks a courier whose other
 * four papers are perfect. So the row carries the **worst** state of the driver's documents, which
 * is the same reduction EXP-003's `documentsGate` makes, in the same order: expired beats
 * unapproved beats approved. Reproducing that order here rather than inventing one keeps the board
 * and the courier's own screen telling the same story; if they disagreed, an administrator would
 * approve a document and watch nothing change on the handset.
 */

/** The scope the listing spends, named so a refusal can say which one was missing. */
export const DRIVER_READ_SCOPE = "driver-profile:read";

/** The scope the documents read spends. */
export const DOCUMENT_READ_SCOPE = "driver-document:read";

/** The scope a decision spends. This board is the only thing on the platform that spends it. */
export const DECISION_SCOPE = "driver-document:verify";

/**
 * The reason code sent with an approval.
 *
 * DRV-03 requires one and publishes no vocabulary for it, so this is the console's own word. It is
 * `^[A-Z][A-Z0-9_]{1,63}$` by contract and otherwise free, which means a reader of the audit log
 * sees whatever a client chose — worth knowing before reading one as though it were an enum.
 */
export const APPROVED_BY_OPERATOR = "APPROVED_BY_TENANT_OPERATOR";

/** And for a rejection. */
export const REJECTED_BY_OPERATOR = "REJECTED_BY_TENANT_OPERATOR";

/** One document, reduced to what this board reads from it. */
export interface DocumentState {
  /** DRV-03 publishes this as `id`, not `documentId`, which the first draft of this assumed. */
  readonly id: string;
  readonly type: string;
  readonly verificationStatus: "pending" | "approved" | "rejected";
  readonly expiresOn?: string | undefined;
  readonly version: number;
}

/**
 * The state of a driver's paperwork as one word.
 *
 * The order is `documentsGate`'s in `services/experience/driver-bff/src/domain/shift-readiness.ts`
 * and not a preference: nothing uploaded, then expired, then not yet approved, then clear. A board
 * that ranked them differently from the gate would show a courier as ready while their handset said
 * otherwise.
 */
/** The five answers a driver's paperwork can give, worst first. */
export type Paperwork = "none" | "expired" | "pending" | "rejected" | "clear";

export function paperworkState(documents: readonly DocumentState[], observedAt: number): Paperwork {
  if (documents.length === 0) return "none";

  const expired = documents.some(
    (document) =>
      document.expiresOn !== undefined &&
      Date.parse(`${document.expiresOn}T00:00:00.000Z`) <= observedAt,
  );
  if (expired) return "expired";

  if (documents.some((document) => document.verificationStatus === "rejected")) return "rejected";
  if (documents.some((document) => document.verificationStatus === "pending")) return "pending";
  return "clear";
}

/**
 * The words the board shows for each state, and the state class each borrows.
 *
 * The classes are the design's own nine, from `@tdelivr/design-tokens` — not a vocabulary
 * invented here, which is what `board.ts` exists to prevent. `failed` for a courier who cannot
 * work, `waiting` for one whose papers are with a reviewer, `done` for one who is cleared, and
 * `unknown` for a documents read that did not answer, which the design reserves for exactly that.
 */
const STATE_WORDS = {
  none: { word: "Nothing uploaded", classKey: "failed" },
  expired: { word: "Expired", classKey: "failed" },
  rejected: { word: "Rejected", classKey: "failed" },
  pending: { word: "Awaiting review", classKey: "waiting" },
  clear: { word: "Cleared", classKey: "done" },
} as const satisfies Readonly<
  Record<Paperwork, { readonly word: string; readonly classKey: StateClassKey }>
>;

/** What an administrator reads in the state column. */
export function stateWords(state: Paperwork): {
  readonly word: string;
  readonly classKey: StateClassKey;
} {
  return STATE_WORDS[state];
}
/**
 * Whether this board can act on a driver at all.
 *
 * A decision needs a document to decide about, so a driver with none offers nothing to press — and
 * saying so on the row is better than a button that fails. This is the same shape as the driver
 * application's own refusals, which name the missing thing rather than the missing permission.
 */
export function decidableDocument(documents: readonly DocumentState[]): DocumentState | undefined {
  return documents.find((document) => document.verificationStatus === "pending");
}

/** One board row, from a profile and whatever its documents turned out to be. */
export function driverRow(
  profile: DriverProfile,
  documents: readonly DocumentState[] | "unread",
  observedAt: number,
  vehicle: VehicleState = "unread",
): BoardRow {
  const paperwork = documents === "unread" ? undefined : paperworkState(documents, observedAt);
  /*
   * Not "Nothing uploaded" when the read failed. A documents read that did not answer and a
   * driver with no documents are different facts, and collapsing them is the
   * absence-reported-as-a-value fault this repository keeps finding — so the row says the read
   * did not answer, in the class the design reserves for not knowing.
   */
  const words =
    paperwork === undefined
      ? ({ word: "Not read", classKey: "unknown" } as const)
      : stateWords(paperwork);

  return {
    id: profile.driverId,
    cells: [
      { text: profile.displayName },
      { text: words.word },
      /*
       * The vehicle beside the paperwork, because they are the two gates a courier is held on and
       * an operator looking at this board is asking one question: can this person work yet.
       */
      vehicleCell(vehicle),
    ],
    state: { classKey: words.classKey, label: words.word },
    detail: [
      { label: "Driver", value: profile.driverId, mono: true },
      { label: "Identity subject", value: profile.userId, mono: true },
      { label: "Operational status", value: profile.operationalStatus },
      {
        label: "Vehicle",
        value:
          vehicle === "unread"
            ? "DRV-04 did not answer for this driver"
            : vehicle === "none"
              ? "none assigned"
              : vehicle.registration,
        ...(vehicle === "unread" || vehicle === "none" ? { absent: true } : {}),
      },
      {
        label: "Documents",
        value:
          documents === "unread"
            ? "DRV-03 did not answer for this driver"
            : documents.length === 0
              ? "none uploaded"
              : documents
                  .map((document) => `${document.type} · ${document.verificationStatus}`)
                  .join(", "),
        ...(documents === "unread" || documents.length === 0 ? { absent: true } : {}),
      },
    ],
    history: [
      { label: "Created", value: profile.createdAt },
      { label: "Last changed", value: profile.updatedAt },
      { label: "Version", value: String(profile.version), mono: true },
    ],
    glyph: "badge",
  };
}
/** DRV-04's create, and the assignment that ties a vehicle to a courier. */
export const VEHICLE_CREATE_SCOPE = "vehicle:create";
export const ASSIGNMENT_WRITE_SCOPE = "vehicle-assignment:write";

/**
 * What an operator types to put a courier on a vehicle.
 *
 * Two records in one form, because DRV-04 models two and an operator does one thing: a
 * vehicle is created and then assigned. Splitting them across two presses would ask somebody
 * to hold a vehicle identifier between steps, and DRV-04 publishes **no vehicle listing** — so
 * a vehicle created and not immediately assigned is one nothing can find again.
 */
export interface VehicleDraft {
  readonly registrationNumber: string;
  readonly vehicleClass: string;
}

/**
 * Whether a vehicle draft can be sent.
 *
 * Both bounds are DRV-04's own. `vehicleClass` is a free string with no published vocabulary,
 * so nothing here restricts it to a list: the design draws motorcycles and cars, and a tenant
 * running tuk-tuks would be refused by a console that had decided there were two kinds of
 * vehicle in the world.
 */
export function vehicleRefusal(draft: VehicleDraft): string | undefined {
  if (draft.registrationNumber.trim().length === 0) {
    return "A registration number is needed.";
  }
  if (draft.vehicleClass.trim().length === 0) {
    return "A vehicle class is needed, such as motorcycle or car.";
  }
  return undefined;
}

/** What this board shows about a courier's vehicle. */
export type VehicleState = "none" | "unread" | { readonly registration: string };

/** The vehicle cell, drawn so that no vehicle and no answer stay different facts. */
export function vehicleCell(state: VehicleState): {
  readonly text: string;
  readonly absent?: true;
  readonly title?: string;
} {
  if (state === "unread") {
    return {
      text: "—",
      absent: true,
      title: "DRV-04 did not answer for this driver",
    };
  }
  if (state === "none") {
    return { text: "None", absent: true, title: "No vehicle is assigned to this courier" };
  }
  return { text: state.registration };
}

/** The states present in the fetched rows, so the filter offers only what it can show. */
export function statesPresent(rows: readonly BoardRow[]): readonly string[] {
  return [...new Set(rows.map((row) => row.state.label))].sort();
}

/**
 * The `Idempotency-Key` for one verification decision, and why the decision is in it.
 *
 * DRV-03 publishes **one** operation for both outcomes — a `verification-decisions` append whose
 * `decision` is `approved` or `rejected`. So a key naming only the driver and the document would be
 * the same key for both, and the service would answer a rejection by replaying the earlier
 * approval: 200, the approved document back, and a board reporting a rejection that never happened.
 *
 * Stated here rather than at the call site for the reason the rest of this module is: a key built
 * inline can only be checked by rendering the component and reading a request, and this is the kind
 * of mistake that draws the right screen while sending the wrong command.
 */
export function decisionKey(
  decision: "approved" | "rejected",
  driverId: string,
  documentId: string,
): string {
  return `tenant-admin/${decision}/${driverId}/${documentId}`;
}

/**
 * Why a rejection cannot be sent yet, or `undefined` when it can.
 *
 * DRV-03 requires `reasonCode` on **every** decision, including a rejection, and refuses without
 * one. That refusal is the one nobody sees: it reaches the operator as a failed request, and the
 * courier — who is the person the reason is written for — is told nothing at all. So the board
 * stops, with the sentence that says who the reason is for.
 */
export function rejectionRefusal(reason: string): string | undefined {
  return reason.trim().length === 0
    ? "DRV-03 requires a reason on every decision, including a rejection. The courier is told it."
    : undefined;
}
